import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { AppetiteModel as Appetite_Model_Enum } from '@risksmart-app/domain/src/types/consts/appetite-model';
import type { ParentType } from '@risksmart-app/domain/src/types/consts/parent-type';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { RiskScoringModel as Risk_Scoring_Model_Enum } from '@risksmart-app/domain/src/types/consts/risk-scoring-model';
import {
  defaultModules,
  isModuleEnabled as resolveModule,
  mergeModulesWithDefaults,
  type Module,
  type ModuleConfig,
  moduleConfigSchema,
  type ModuleKey,
} from '@risksmart-app/modules/src/index';
import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';
import { create } from 'zustand';

import { useUpdateAggregationSettingsForOrg } from '@/hooks/mutations/aggregation';
import { useUpdateModules } from '@/hooks/mutations/organisation-module';
import { useResetTabPreferences } from '@/hooks/mutations/tab-preferences';
import { useGetModules } from '@/hooks/queries';

export type BasicModule = Module;

const parseModuleSettings = (raw: unknown): ModuleConfig => {
  const result = moduleConfigSchema.safeParse(raw);

  return result.success ? result.data : {};
};

export const parseJsonConfig = (
  raw: string | undefined | null
): Record<string, unknown> => {
  try {
    const parsed: unknown = JSON.parse(raw ?? '{}');

    return _.isPlainObject(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

export type Modules = {
  toggle: (id: string) => void;
  /**
   * Reads the raw zustand module store. For checking if a module is
   * enabled in components, prefer `useIsModuleEnabled` or
   * `useIsModuleEnabledLazy` from `src/hooks/useIsModuleEnabled`.
   */
  isModuleEnabled: (id: ModuleKey) => boolean;
  setConfig: (id: string, config: Record<string, unknown>) => void;
  modules: Record<string, BasicModule>;
  savedModules: Record<string, BasicModule> | null;
  serverModules: Record<string, BasicModule> | null;
  hydrated: boolean;
  isDirty: boolean;
  setIsDirty: (isDirty: boolean) => void;
  dirtyModules?: string[];
  hydrateFromServer: (raw: unknown) => void;
  commitModules: () => void;
  resetToSaved: () => boolean;
};

// Default modules are now defined in @risksmart-app/modules.
// See packages/modules/src/defaults.ts for the authoritative source.

/**
 * Set of module IDs that correspond to valid Parent_Type_Enum values.
 * Only these modules can have their tab preferences reset.
 */
const PARENT_TYPE_MODULES = new Set<ParentType>(
  Object.values(Parent_Type_Enum)
);

const isParentTypeEnum = (value: string): value is ParentType => {
  return PARENT_TYPE_MODULES.has(value as ParentType);
};

export const useModulesStore = create<Modules>((set, get) => {
  return {
    toggle: (id: string) => {
      set((state) => {
        const updatedModules = _.cloneDeep(state.modules);

        if (_.has(updatedModules, id)) {
          const currentEnabled = _.get(updatedModules, `${id}.enabled`, false);
          const newEnabled = !currentEnabled;

          _.set(updatedModules, `${id}.enabled`, newEnabled);

          // If disabling a module, also disable all its submodules
          if (!newEnabled) {
            const module = _.get(updatedModules, id) as BasicModule | undefined;
            if (module?.subModules) {
              Object.keys(module.subModules).forEach((subModuleKey) => {
                _.set(
                  updatedModules,
                  `${id}.subModules.${subModuleKey}.enabled`,
                  false
                );
              });
            }
          }
        } else {
          console.warn(`Module with id "${id}" does not exist.`);
        }

        const mainModuleId = id.split('.')[0];

        return {
          modules: updatedModules,
          isDirty: true,
          dirtyModules: _.uniq(
            _.concat(state.dirtyModules ?? [], mainModuleId)
          ),
        };
      });
    },
    setConfig: (id: string, config: Record<string, unknown>) => {
      set((state) => {
        const updatedModules = { ...state.modules };
        if (_.has(updatedModules, id)) {
          _.set(updatedModules, `${id}.config`, config);
        } else {
          console.warn(`Module with id "${id}" does not exist.`);
        }

        return { modules: updatedModules, isDirty: true };
      });
    },
    isModuleEnabled: (id: ModuleKey): boolean => {
      return resolveModule(get().modules, id);
    },
    modules: defaultModules,
    savedModules: null,
    serverModules: null,
    hydrated: false,
    dirtyModules: [],
    // @TODO: Obsolete, can be inferred from dirtyModules
    isDirty: false,
    setIsDirty: (isDirty: boolean) => {
      set({ isDirty, dirtyModules: isDirty ? get().dirtyModules : [] });
    },
    hydrateFromServer: (raw: unknown) => {
      set((state) => {
        if (state.isDirty) {
          return {};
        }

        const incoming = mergeModulesWithDefaults(
          raw ? parseModuleSettings(raw) : {}
        );

        if (state.serverModules && _.isEqual(state.serverModules, incoming)) {
          return state.hydrated ? {} : { hydrated: true };
        }

        // Cloned, not aliased: `setConfig` mutates `modules` in place.
        return {
          modules: incoming,
          savedModules: _.cloneDeep(incoming),
          serverModules: _.cloneDeep(incoming),
          hydrated: true,
        };
      });
    },
    commitModules: () => {
      set((state) => ({
        savedModules: _.cloneDeep(state.modules),
        isDirty: false,
        dirtyModules: [],
      }));
    },
    resetToSaved: () => {
      const { savedModules } = get();
      if (!savedModules) {
        return false;
      }
      set({
        modules: _.cloneDeep(savedModules),
        isDirty: false,
        dirtyModules: [],
      });

      return true;
    },
  };
});

const useHydratedModulesQuery = () => {
  const isDirty = useModulesStore((state) => state.isDirty);
  const hydrateFromServer = useModulesStore((state) => state.hydrateFromServer);
  const query = useGetModules({ queryArgs: {} });
  const { data, loading } = query;

  useEffect(() => {
    if (!loading) {
      hydrateFromServer(data?.organisation_module?.[0]?.ModuleSettings);
    }
  }, [loading, data, isDirty, hydrateFromServer]);

  return query;
};

export const useModules = () => {
  const {
    toggle,
    isModuleEnabled,
    modules,
    setConfig,
    isDirty,
    dirtyModules,
    commitModules,
    resetToSaved,
  } = useModulesStore();
  const { addNotification } = useNotifications();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { updateAggregationSettingsForOrg: updateAggregationSettings } =
    useUpdateAggregationSettingsForOrg();
  const [updateModules, { loading: updateLoading }] = useUpdateModules();
  const { loading, refetch } = useHydratedModulesQuery();
  const [resetTabs] = useResetTabPreferences();

  const commit = async (config: {
    RiskScoringModel: Risk_Scoring_Model_Enum;
    RiskScoringModelConfig: string;
    AppetiteCascadingModel: Appetite_Model_Enum;
    AppetiteCascadingModelConfig: string;
  }) => {
    await updateModules({
      variables: { ModuleSettings: modules },
    });
    addNotification({
      type: 'success',
      content: 'Modules updated successfully.',
    });

    if (dirtyModules?.length) {
      await Promise.all(
        dirtyModules
          .filter((c) => isParentTypeEnum(c))
          .map(async (moduleId: ParentType) => {
            return await resetTabs({
              variables: {
                ObjectType: moduleId,
              },
            });
          })
      );
    }

    const riskScoringEnabled = isModuleEnabled('risk.subModules.risk_scoring');
    const appetiteCascadingEnabled = isModuleEnabled(
      'risk.subModules.appetite_cascading'
    );

    if (riskScoringEnabled || appetiteCascadingEnabled) {
      await updateAggregationSettings({
        RiskScoringModel:
          riskScoringEnabled && config.RiskScoringModel
            ? config.RiskScoringModel
            : null,
        AppetiteCascadingModel:
          appetiteCascadingEnabled && config.AppetiteCascadingModel
            ? config.AppetiteCascadingModel
            : null,
        Config: {
          ...parseJsonConfig(
            riskScoringEnabled ? config.RiskScoringModelConfig : '{}'
          ),
          ...parseJsonConfig(
            appetiteCascadingEnabled
              ? config.AppetiteCascadingModelConfig
              : '{}'
          ),
        },
      });
    } else {
      await updateAggregationSettings({
        RiskScoringModel: null,
        AppetiteCascadingModel: null,
        Config: {},
      });
    }

    await queryClient.invalidateQueries({
      queryKey:
        trpc.frontend.aggregation.getAggregationSettingsForOrg.queryKey(),
    });

    commitModules();

    // `useGetModules` is `no-cache`, so `evictField` cannot refresh it (COI-820).
    await refetch().catch(() => undefined);
  };

  const reset = () => {
    if (resetToSaved()) {
      addNotification({
        type: 'success',
        content: 'Changes reverted.',
      });
    }
  };

  return {
    toggle,
    isModuleEnabled,
    modules,
    commit,
    reset,
    loading: updateLoading || loading,
    setConfig,
    isDirty,
  };
};

/**
 * Lightweight hook that hydrates the module store from the server.
 * Call this early in the component tree (e.g. ProtectedLayout) so
 * that ModuleGatedRoute has org-specific module state before it
 * decides whether to render or throw.
 *
 * Mounting this alongside `useModules` does not share one request:
 * the Apollo path is `no-cache`, so each hook holds its own read.
 */
export const useModulesHydration = () => {
  const hydrated = useModulesStore((state) => state.hydrated);
  const { loading } = useHydratedModulesQuery();

  return { hydrated: hydrated || loading === false, loading };
};
